/**
 * ShopifyInstallIntercept
 *
 * Shopify's managed-install flow (the default for new apps when
 * `Use legacy install flow=false` and `embedded=false`) redirects the merchant
 * to the configured App URL after install with these query params:
 *
 *   ?hmac=…&host=…&shop=<shop>.myshopify.com&timestamp=…
 *
 * Until this component existed, that URL was the marketing homepage `/`, which
 * does not recognise the params — the merchant saw "Start Free" instead of the
 * app. Shopify's automated install check correctly flagged the post-install
 * state as a critical UI bug (Functionality: Not met) and the App Store
 * listing-check spinner stayed stuck.
 *
 * This component runs once on mount, detects the Shopify managed-install
 * load shape, and bounces the merchant to the existing public OAuth
 * initiator (`/api/integrations/shopify/install?shop=…`). The OAuth callback
 * already redirects to `/signup?shopify=connected&shop=…` (see
 * `server/src/routes/integrations/shopifyRoutes.ts:51`+), which triggers the
 * PR #91 finalize handler in `Signup.tsx`.
 *
 * Tracked in `server/docs/shopify-golive-2026-06-02.md`.
 */
import { useEffect, type ReactElement } from 'react';

interface Props {
  children: ReactElement;
}

const SHOP_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/;

function getApiBase(): string {
  const fromEnv = import.meta.env.VITE_API_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    return fromEnv.replace(/\/+$/, '');
  }
  return 'https://api.buzzbitx.com';
}

export function ShopifyInstallIntercept({ children }: Props): ReactElement {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hmac = params.get('hmac');
    const host = params.get('host');
    const shop = params.get('shop');
    const timestamp = params.get('timestamp');

    if (!hmac || !host || !shop || !timestamp) return;
    if (!SHOP_REGEX.test(shop)) return;

    // Shopify managed-install load shape: bounce through the existing public
    // OAuth initiator. The backend re-validates the shop, generates state,
    // parks it in Redis, and redirects to Shopify's grant screen. After
    // approval, the OAuth callback redirects to /signup?shopify=connected
    // where the PR #91 finalize handler completes the connection.
    // The WHOLE query goes along: Shopify signed every parameter, and the
    // initiator verifies that signature when it sees one.
    const base = getApiBase();
    window.location.replace(`${base}/api/integrations/shopify/install?${params.toString()}`);
  }, []);

  return children;
}
